import { useMemo, useState } from 'react';
import { Image, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, control, fontFamily, price, radii, shadows, space, type } from '~/ui/tokens';
import { formatMoney } from '~/lib/money';
import { getAmenityIcon } from '~/lib/amenityIcons';
import { decisionAmenities, shortRoomType } from '~/lib/hotelPresentation';
import { areaLabel } from '~/lib/location';
import { formatHotelPageReviewScore, getHotelReviewData } from '~/lib/hotelReviews';
import { useCurrency } from '~/stores/useCurrency';
import type { HotelSummary, ImageSource } from '~/lib/api/types';

type HotelRowProps = {
  hotel: HotelSummary;
  onPress: () => void;
  isMember: boolean;
  onUnlock: () => void;
  nights: number;
  adults: number;
  href?: string;
};

export function HotelRow({ hotel, onPress, isMember, onUnlock, nights, adults }: HotelRowProps) {
  useCurrency((state) => state.selectedCurrency);
  const { width } = useWindowDimensions();
  const stacked = width < 760;
  const [photoIndex, setPhotoIndex] = useState(0);
  const [failedPhotos, setFailedPhotos] = useState<number[]>([]);
  const [photoHover, setPhotoHover] = useState(false);
  const offer = hotel.searchOffer;
  const amenities = useMemo(() => decisionAmenities(hotel.amenities), [hotel.amenities]);
  const roomType = shortRoomType(offer?.roomName);
  const location = hotel.address ? areaLabel(hotel.address) : null;
  const reviewData = useMemo(() => getHotelReviewData(hotel), [hotel]);
  const reviewScoreFormatted = reviewData ? formatHotelPageReviewScore(reviewData.score) : null;
  const totalPrice = offer?.totalPrice ?? {
    ...hotel.lowestPrice,
    amount: String(Number(hotel.lowestPrice.amount) * Math.max(1, nights)),
  };
  // Use the same selected whole-stay price for both labels, including cached
  // search results built before the server's average-night correction.
  const nightlyPrice = offer?.totalPrice && Number(offer.totalPrice.amount) > 0
    ? { ...offer.totalPrice, amount: String(Number(offer.totalPrice.amount) / Math.max(1, nights)) }
    : hotel.lowestPrice;
  const cancellationLabel = offer?.freeCancellationBefore ? 'Free cancellation' : 'No free cancellation';
  const breakfastLabel = offer?.hasBreakfast ? 'Breakfast included' : 'Breakfast not included';

  // Only the hotel's own photo; when it is missing or fails to load, show the
  // "Photo unavailable" slate. Stock photography would misrepresent the hotel.
  const photos = useMemo(
    () => (hotel.images?.length ? hotel.images : [hotel.thumbnail].filter(Boolean)) as ImageSource[],
    [hotel.images, hotel.thumbnail],
  );
  const imageSource = failedPhotos.includes(photoIndex) ? undefined : photos[photoIndex];
  const showArrows = photos.length > 1 && (stacked || photoHover);
  function stepPhoto(delta: number) {
    setPhotoIndex((index) => (index + delta + photos.length) % photos.length);
  }

  const hotelArea = (
    <View style={[styles.hotelArea, stacked && styles.hotelAreaStacked]}>
      <View
        style={[styles.imageWrap, stacked && styles.imageWrapStacked]}
        {...({ onMouseEnter: () => setPhotoHover(true), onMouseLeave: () => setPhotoHover(false) } as object)}
      >
        {imageSource ? (
          <Image
            source={imageSource}
            style={styles.image}
            resizeMode="cover"
            accessibilityLabel={`${hotel.name} hotel`}
            onError={() => setFailedPhotos((failed) => [...failed, photoIndex])}
          />
        ) : (
          <View style={[styles.image, styles.imageFallback]}>
            <Ionicons name="business-outline" size={26} color={colors.inkFaint} />
            <Text style={styles.imageFallbackText}>Photo unavailable</Text>
          </View>
        )}
        {showArrows ? (
          <>
            <Pressable accessibilityRole="button" accessibilityLabel="Previous photo" onPress={() => stepPhoto(-1)} hitSlop={8} style={[styles.photoArrow, styles.photoArrowLeft]}>
              <Ionicons name="chevron-back" size={16} color={colors.ink} />
            </Pressable>
            <Pressable accessibilityRole="button" accessibilityLabel="Next photo" onPress={() => stepPhoto(1)} hitSlop={8} style={[styles.photoArrow, styles.photoArrowRight]}>
              <Ionicons name="chevron-forward" size={16} color={colors.ink} />
            </Pressable>
          </>
        ) : null}
        {photos.length > 1 ? (
          <View style={styles.imageDots}>
            {photos.map((_, index) => <View key={index} style={index === photoIndex ? styles.imageDotActive : styles.imageDot} />)}
          </View>
        ) : null}
      </View>

      <View style={stacked ? styles.infoStacked : styles.info}>
        <View style={!stacked ? styles.starSlot : undefined}>
          {hotel.starRating > 0 ? (
            <View style={styles.stars} accessibilityLabel={`${hotel.starRating} star hotel`}>
              {Array.from({ length: Math.min(5, hotel.starRating) }).map((_, index) => (
                <Ionicons key={index} name="star" size={12} color="#F59E0B" />
              ))}
            </View>
          ) : null}
        </View>
        <View style={[!stacked && styles.nameSlot, stacked && hotel.starRating > 0 && styles.nameSlotStacked]}>
          <Text style={styles.name} numberOfLines={2}>{hotel.name}</Text>
        </View>
        <View style={[!stacked && styles.addressSlot, stacked && location && styles.addressSlotStacked]}>
          {location ? (
            <View style={styles.addressRow}>
              <Ionicons name="location-outline" size={14} color={colors.inkFaint} style={styles.addressIcon} />
              <Text style={styles.address} numberOfLines={2}>{location}</Text>
            </View>
          ) : null}
        </View>
        <View style={[!stacked && styles.roomSlot, stacked && roomType && (location ? styles.roomSlotStacked : styles.roomSlotAfterNameStacked)]}>
          {roomType ? (
            <View style={styles.roomChip}>
              <Ionicons name="bed-outline" size={14} color={colors.inkSoft} />
              <Text style={styles.room} numberOfLines={1}>{roomType}</Text>
            </View>
          ) : null}
        </View>
        <View style={[!stacked && styles.amenitiesSlot, stacked && amenities.length > 0 && (roomType ? styles.amenitiesSlotStacked : styles.amenitiesSlotWithoutRoomStacked)]}>
          {amenities.length > 0 ? (
            <View style={styles.amenities}>
              {amenities.slice(0, 3).map((amenity) => (
                <View key={amenity} style={styles.amenityItem}>
                  <Ionicons name={getAmenityIcon(amenity)} size={14} color={colors.inkSoft} />
                  <Text style={styles.amenity} numberOfLines={1}>{amenity}</Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>
        <View style={[!stacked && styles.policySlot, stacked && styles.policySlotStacked]}>
          <View style={styles.policies}>
            <View style={styles.policyItem}>
              <Ionicons name={offer?.freeCancellationBefore ? 'shield-checkmark-outline' : 'close-circle-outline'} size={15} color={offer?.freeCancellationBefore ? colors.green : colors.inkFaint} />
              <Text style={offer?.freeCancellationBefore ? styles.policyPositive : styles.policyMuted} numberOfLines={1}>{cancellationLabel}</Text>
            </View>
            <View style={styles.policyItem}>
              <Ionicons name="restaurant-outline" size={15} color={offer?.hasBreakfast ? colors.green : colors.inkFaint} />
              <Text style={offer?.hasBreakfast ? styles.policyPositive : styles.policyMuted} numberOfLines={1}>{breakfastLabel}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  const ctaButton = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`View rooms at ${hotel.name}`}
      onPress={isMember ? onPress : onUnlock}
      style={({ pressed }) => StyleSheet.flatten([styles.cta, pressed && styles.ctaPressed])}
    >
      <Text style={styles.ctaText}>View rooms</Text>
      <Ionicons name="chevron-forward" size={15} color={colors.white} />
    </Pressable>
  );

  return (
    <View style={styles.card}>
      <View style={[styles.body, stacked && styles.bodyStacked]}>
        {hotelArea}

        <View style={[styles.priceModule, stacked && styles.priceModuleStacked]}>
          {reviewData ? (
            <View style={styles.reviewSummary} accessibilityLabel={[reviewData.word, reviewScoreFormatted, reviewData.countFormatted].filter(Boolean).join(', ')}>
              <View style={styles.reviewCopy}>
                <Text style={styles.reviewWord}>{reviewData.word}</Text>
                {reviewData.countFormatted ? <Text style={styles.reviewCount}>{reviewData.countFormatted}</Text> : null}
              </View>
              <View style={styles.reviewScoreBadge}>
                <Text style={styles.reviewScoreText}>{reviewScoreFormatted}</Text>
              </View>
            </View>
          ) : (
            <View style={styles.reviewSummary}>
              <View style={styles.reviewCopy}>
                <Text style={styles.reviewCount}>No reviews yet</Text>
              </View>
            </View>
          )}
          <View style={styles.priceAction}>
            <View style={styles.priceDecision}>
              <Text style={styles.staySummary}>{nights} {nights === 1 ? 'night' : 'nights'} · {adults} {adults === 1 ? 'adult' : 'adults'}</Text>
              <View style={styles.priceRow}>
                <Text style={styles.price}>{formatMoney(nightlyPrice)}</Text>
                <Text style={styles.night}>avg/night</Text>
              </View>
              <Text style={styles.total}>{formatMoney(totalPrice)} total</Text>
            </View>
            {ctaButton}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, overflow: 'hidden', ...shadows.sm },
  body: { minHeight: 244, flexDirection: 'row', padding: space.component, gap: space.internal },
  bodyStacked: { flexDirection: 'column' },
  hotelArea: { flex: 1, minWidth: 0, flexDirection: 'row', gap: space.internal },
  hotelAreaStacked: { flexDirection: 'column' },
  imageWrap: { width: 224, height: 212, position: 'relative', flexShrink: 0 },
  imageWrapStacked: { width: '100%', height: 168 },
  image: { width: '100%', height: '100%', borderRadius: radii.sm, backgroundColor: colors.lineSoft },
  imageFallback: { alignItems: 'center', justifyContent: 'center', gap: space.micro },
  imageFallbackText: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  photoArrow: { position: 'absolute', top: '50%', marginTop: -16, width: 32, height: 32, borderRadius: radii.full, backgroundColor: 'rgba(255,255,255,0.92)', alignItems: 'center', justifyContent: 'center', ...shadows.sm },
  photoArrowLeft: { left: space.compact },
  photoArrowRight: { right: space.compact },
  imageDots: { pointerEvents: 'none', position: 'absolute', left: 0, right: 0, bottom: space.compact, flexDirection: 'row', justifyContent: 'center', gap: space.micro },
  imageDot: { width: 7, height: 7, borderRadius: radii.full, backgroundColor: 'rgba(255,255,255,0.7)' },
  imageDotActive: { width: 7, height: 7, borderRadius: radii.full, backgroundColor: colors.white },
  info: { flex: 1, minWidth: 0, height: 212, alignSelf: 'flex-start', overflow: 'hidden' },
  infoStacked: { minWidth: 0, width: '100%', alignSelf: 'stretch' },
  starSlot: { height: 16, justifyContent: 'center' },
  stars: { flexDirection: 'row', gap: 2 },
  nameSlot: { height: 44, marginTop: 4 },
  nameSlotStacked: { marginTop: 4 },
  name: { fontFamily: fontFamily.bold, ...type.cardTitle, color: colors.ink },
  addressSlot: { height: 40, marginTop: 8 },
  addressSlotStacked: { marginTop: 8 },
  addressRow: { flexDirection: 'row', alignItems: 'flex-start', gap: space.micro },
  addressIcon: { marginTop: 2 },
  address: { flex: 1, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  roomSlot: { height: 20, marginTop: 8 },
  roomSlotStacked: { marginTop: 12 },
  roomSlotAfterNameStacked: { marginTop: 8 },
  roomChip: { flexDirection: 'row', alignItems: 'center', gap: space.micro },
  room: { flex: 1, fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  amenitiesSlot: { height: 16, marginTop: 8 },
  amenitiesSlotStacked: { marginTop: 8 },
  amenitiesSlotWithoutRoomStacked: { marginTop: 12 },
  amenities: { minWidth: 0, flexDirection: 'row', flexWrap: 'nowrap', columnGap: space.compact },
  amenityItem: { minWidth: 0, maxWidth: '100%', flexShrink: 1, flexDirection: 'row', alignItems: 'center', gap: space.micro },
  amenity: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  policySlot: { height: 16, marginTop: 12 },
  policySlotStacked: { marginTop: 12 },
  policies: { minWidth: 0, flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: space.compact },
  policyItem: { minWidth: 0, flexShrink: 1, flexDirection: 'row', alignItems: 'center', gap: space.micro },
  policyPositive: { flexShrink: 1, fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.green },
  policyMuted: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  priceModule: { width: 220, height: 212, flexShrink: 0, borderLeftWidth: 1, borderLeftColor: colors.lineSoft, paddingLeft: space.internal, alignItems: 'stretch', justifyContent: 'space-between' },
  priceModuleStacked: { width: '100%', borderLeftWidth: 0, borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingLeft: 0, paddingTop: space.compact },
  reviewSummary: { minHeight: 50, flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'flex-end', gap: space.related },
  reviewCopy: { flex: 1, alignItems: 'flex-end', minWidth: 0 },
  reviewWord: { fontFamily: fontFamily.semibold, ...type.bodyRegular, color: '#1F2937', textAlign: 'right' },
  reviewCount: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: '#64748B', textAlign: 'right' },
  reviewScoreBadge: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: radii.sm, backgroundColor: '#243247' },
  reviewScoreText: { fontFamily: fontFamily.bold, fontSize: 17, color: '#FFFFFF', fontVariant: ['tabular-nums'] },
  priceAction: { width: '100%', alignItems: 'stretch', gap: space.compact },
  priceDecision: { width: '100%', alignItems: 'flex-end', gap: space.micro },
  staySummary: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft, textAlign: 'right' },
  priceRow: { flexDirection: 'row', alignItems: 'baseline', gap: space.micro },
  price: { fontFamily: fontFamily.bold, ...price.listing, color: colors.ink },
  night: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  total: { fontFamily: fontFamily.semibold, ...type.metadata, color: colors.ink },
  cta: { width: '100%', minHeight: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: space.micro, backgroundColor: colors.brand, borderRadius: radii.sm, paddingHorizontal: space.compact },
  ctaPressed: { backgroundColor: colors.brandDeep, opacity: 0.88 },
  ctaText: { fontFamily: fontFamily.semibold, fontSize: control.cardCta.fontSize, color: colors.white },
});
